/**
 * Phase 270 — Adoption-Nudge Console (mounted inside the Super Admin dashboard).
 *
 * Two-step UX:
 *   1. "Analyze Accounts" → preview-mode scan, surfaces stuck academies.
 *   2. "Dispatch Nudges" → routes each result through Phase 267 outreach
 *      (channel=email, template_name=adoption_{milestone}) — inherits the
 *      Phase 267 audit log + Resend POST + cooldown machinery for free.
 *
 * Cooldown is 7-day MILESTONE-specific (server-enforced) — different
 * milestones live in different cooldown buckets so adoption progress
 * unblocks the next nudge naturally.
 */
import { useState } from 'react';
import { RefreshCw, Send, AlertCircle, CheckCircle2, Loader2, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ResendDomainPill from './ResendDomainPill';

export default function AdoptionNudgeConsole() {
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [scan, setScan] = useState(null);
  const [dispatchSummary, setDispatchSummary] = useState(null);
  // Phase 270c — sender-domain verification gating
  const [senderStatus, setSenderStatus] = useState(null);
  const senderVerified = senderStatus?.sender_verified === true;

  const runScan = async () => {
    setLoading(true);
    setDispatchSummary(null);
    try {
      const r = await api.post('/super-admin/adoption/scan');
      setScan(r.data);
      if (r.data.matched_count === 0) {
        const reason = Object.entries(r.data.skipped || {})
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .join(' · ');
        toast.message(reason ? `No academies need a nudge — ${reason}` : 'No academies need a nudge right now.');
      } else {
        toast.success(`Found ${r.data.matched_count} academies needing onboarding nudges.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally { setLoading(false); }
  };

  const runDispatch = async () => {
    if (!scan || scan.matched_count === 0) return;
    if (!senderVerified) {
      toast.error('Sender domain not verified on Resend — dispatch blocked. Complete DNS verification first.');
      return;
    }
    if (!window.confirm(`Dispatch ${scan.matched_count} adoption nudges via email? Each recipient is on a 7-day cooldown per milestone — already-recent sends will be skipped server-side.`)) return;
    setDispatching(true);
    try {
      const r = await api.post('/super-admin/adoption/scan?dispatch=true');
      setScan(r.data);
      setDispatchSummary({
        attempted: r.data.matched_count,
        dispatched: r.data.dispatched_count,
      });
      if (r.data.dispatched_count > 0) {
        toast.success(`Dispatched ${r.data.dispatched_count} of ${r.data.matched_count} adoption nudges.`);
      } else {
        toast.error('No nudges dispatched — check audit log for details.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dispatch failed');
    } finally { setDispatching(false); }
  };

  const milestoneLabel = (m) => ({
    student_creation: 'Stuck: no students',
    employee_creation: 'Stuck: no coaches',
    fee_management: 'Stuck: no fees configured',
  }[m] || m);

  const milestoneColor = (m) => ({
    student_creation: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900',
    employee_creation: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900',
    fee_management: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900',
  }[m] || 'bg-muted text-muted-foreground border-border');

  return (
    <Card data-testid="adoption-nudge-console" className="border-2 border-indigo-200 dark:border-indigo-900">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #6366f1, #a78bfa)' }}>
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold flex items-center gap-2">
                Adoption-Nudge Engine
                <Badge variant="secondary" className="text-[9px]">NEW</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                Scans TRIAL / ACTIVE academies for stuck onboarding milestones (no students → no coaches → no fees) and dispatches step-by-step email walkthroughs via the Phase 267 outreach engine. 7-day cooldown per milestone.
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={runScan}
              disabled={loading || dispatching}
              data-testid="adoption-scan-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Analyze Accounts
            </Button>
            {scan?.matched_count > 0 && (
              <Button
                size="sm"
                onClick={runDispatch}
                disabled={dispatching || !senderVerified}
                data-testid="adoption-dispatch-btn"
                title={senderVerified ? '' : 'Sender domain unverified — fix DNS in Resend first'}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {dispatching ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Dispatch ({scan.matched_count})
              </Button>
            )}
          </div>
        </div>

        {/* Phase 270c — sender-domain verification gate */}
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/20 p-2" data-testid="adoption-sender-gate">
          <ResendDomainPill onStatusChange={setSenderStatus} testIdPrefix="adoption-sender" />
          {senderStatus && !senderVerified && (
            <span className="text-[10px] text-rose-600 dark:text-rose-400 font-medium">
              Dispatch blocked until verified
            </span>
          )}
        </div>

        {dispatchSummary && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-2.5 text-[11px] text-emerald-800 dark:text-emerald-200" data-testid="adoption-dispatch-summary">
            <CheckCircle2 className="h-3 w-3 inline mr-1" /> <strong>{dispatchSummary.dispatched}/{dispatchSummary.attempted}</strong> nudges dispatched · audit trail in CRM Directory → Audit Log
          </div>
        )}

        {scan && scan.results.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-left text-xs" data-testid="adoption-results-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-[10px] uppercase text-muted-foreground">
                  <th className="py-2 px-3">Academy</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Milestone</th>
                  <th className="py-2 px-3">Adoption</th>
                  <th className="py-2 px-3">Owner</th>
                  {scan.mode === 'dispatch' && <th className="py-2 px-3">Dispatch</th>}
                </tr>
              </thead>
              <tbody>
                {scan.results.map((r) => (
                  <tr key={r.academy_id} className="border-b border-border/40" data-testid={`adoption-row-${r.academy_id}`}>
                    <td className="py-2 px-3 font-medium">{r.academy_name}</td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-[10px]">{r.subscription_status}</Badge>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-md border ${milestoneColor(r.milestone)}`}>
                        {milestoneLabel(r.milestone)}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px] text-muted-foreground">
                      S:{r.adoption_state.students} · C:{r.adoption_state.coaches} · F:{r.adoption_state.fees}
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px]">{r.owner_email}</td>
                    {scan.mode === 'dispatch' && (
                      <td className="py-2 px-3">
                        {r.dispatch_status === 'sent' ? (
                          <Badge className="text-[10px] bg-emerald-600">sent</Badge>
                        ) : r.dispatch_status === 'failed' ? (
                          <Badge variant="destructive" className="text-[10px]" title={r.dispatch_error || ''}>failed</Badge>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {scan && scan.matched_count === 0 && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-[11px] text-muted-foreground" data-testid="adoption-empty-state">
            <AlertCircle className="h-3 w-3 inline mr-1" />
            No academies need a nudge.
            {' '}
            Skipped breakdown: {Object.entries(scan.skipped || {}).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
